import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import {
  Dialog as MUIDialog,
  DialogContent,
  Grid,
  Typography,
  DialogActions,
} from "@mui/material";

import ConfirmPopup from "./ConfirmPopup";
import { useAxios } from "../../../hooks";
import { colors } from "../../css components/Style";
import { CancelButton, PrimaryButton } from "../../css components/Button";
import { alert } from "../../../redux/slices/alertSlice";
import pages from "../../../constants/pages";
import { titleCase } from "change-case-all";

export default function ReportDialog({ reportDialog, setReportDialog }) {
  const axios = useAxios();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ state: false });

  useEffect(() => {
    if (reportDialog.state) {
      axios({
        url: "/external/campaign/report/detail/" + reportDialog.campaignID,
        method: "POST",
        disableRedirect: true,
      }).then((response) => {
        if (response.status) {
          setFormData(response.data);
        }
      });
    }
  }, [axios, reportDialog.campaignID, reportDialog.state]);

  const handleClose = () => {
    setReportDialog({ state: false });
  };

  const renderField = (field, index) => {
    const excludeKeys = [
      "mandatory",
      "is_additional",
      "userCount",
      "field_id",
      "is_edit",
      "percentage",
    ];

    return Object.entries(field)
      .filter(([key]) => !excludeKeys.includes(key))
      .map(([key, value]) => (
        <Fragment>
          <Grid size={4}>
            <Typography fontWeight="bold">
              {titleCase(key.replace(/_/g, " "))}
            </Typography>
          </Grid>
          <Grid size={8}>
            <Typography>
              {Array.isArray(value)
                ? value.join(", ")
                : value &&
                  typeof value === "object" &&
                  "min" in value &&
                  "max" in value
                ? `Min: ${value.min}, Max: ${value.max}`
                : String(value)}
            </Typography>
          </Grid>
        </Fragment>
      ));
  };

  return (
    <Fragment>
      <MUIDialog open={reportDialog.state} onClose={handleClose}>
        <DialogContent style={{ padding: "24px" }}>
          <Grid container spacing={4}>
            <Grid size={12}>
              <Typography variant="h4" color={colors.publicisBlue}>
                Campaign Report Details
              </Typography>
            </Grid>

            <Grid size={4}>
              <Typography fontWeight="bold">Campaign Name</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{formData.campaign_name}</Typography>
            </Grid>

            <Grid size={4}>
              <Typography fontWeight="bold">Campaign Period</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{formData.campaign_period}</Typography>
            </Grid>

            <Grid size={4}>
              <Typography fontWeight="bold">Audience Name</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{formData.audience_name}</Typography>
            </Grid>

            <Grid size={4}>
              <Typography fontWeight="bold">Audience Size</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{formData.audience_size}</Typography>
            </Grid>

            <Grid size={4}>
              <Typography fontWeight="bold">Audience CPM</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{formData.audience_cpm}</Typography>
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" color={colors.publicisBlue}>
                Audience Fields
              </Typography>
            </Grid>

            {formData.audience_fields?.fields?.map((field, index) =>
              renderField(field, index)
            )}
          </Grid>
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px" }}>
          <PrimaryButton
            onClick={() => {
              setDialog({ state: true });
            }}
            variant="contained"
          >
            Reactivate Campaign
          </PrimaryButton>
          <CancelButton variant="outlined" onClick={handleClose}>
            Cancel
          </CancelButton>
        </DialogActions>
      </MUIDialog>
      <ConfirmPopup
        dialog={dialog}
        setDialog={setDialog}
        reactivateCampaign={reactivateCampaign}
        loading={loading}
      />
    </Fragment>
  );

  function reactivateCampaign() {
    setLoading(true);
    axios({
      url: "/reactivate/campaign/" + formData.campaign_id,
      disableRedirect: true,
      method: "POST",
    }).then((res) => {
      if (res.status) {
        dispatch(
          alert({
            type: "success",
            message: res.message.displayMessage,
          })
        );
        navigate(
          pages.campaignDetails.route + res.data?.campaign_id + "?mode=view"
        );
      }
      setLoading(false);
      setDialog({ state: false });
    });
  }
}
